import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Hourglass, Send, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = { waiting: "Wartet", notified: "Benachrichtigt", booked: "Gebucht", expired: "Abgelaufen" };
const statusCls = (s) =>
  s === "notified" ? "bg-terracotta/10 text-terracotta" :
  s === "booked" ? "bg-sage/15 text-sage" :
  s === "expired" ? "bg-ink/10 text-ink-muted" :
  "bg-terracotta/15 text-terracotta";

export default function AdminWaitlist() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("waiting");

  const load = async () => { const { data } = await api.get("/admin/waitlist"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const notify = async (id) => {
    try { await api.post(`/admin/waitlist/${id}/notify`); toast.success("E-Mail versendet"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (id, status) => {
    try { await api.put(`/admin/waitlist/${id}/status`, { status }); toast.success("✓"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Löschen?")) return;
    await api.delete(`/admin/waitlist/${id}`);
    load();
  };

  const filtered = items.filter((w) => filter === "all" || w.status === filter);

  return (
    <div className="p-5 sm:p-8 lg:p-10" data-testid="admin-waitlist-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display text-3xl sm:text-4xl text-ink flex items-center gap-3"><Hourglass className="w-6 h-6 sm:w-7 sm:h-7 text-terracotta" strokeWidth={1.5} /> Warteliste</h1>
        <div className="flex gap-2 text-xs overflow-x-auto pb-1 w-full sm:w-auto">
          {["waiting", "notified", "booked", "expired", "all"].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`shrink-0 px-3 py-1.5 rounded-full uppercase tracking-wider ${filter === f ? "bg-ink text-white" : "bg-white border border-border text-ink-muted"}`}
              data-testid={`wl-filter-${f}`}>
              {f === "all" ? "Alle" : STATUS_LABEL[f]}
            </button>
          ))}
        </div>
      </div>

      <p className="mt-4 text-sm text-ink-muted max-w-3xl">
        Wenn du eine Buchung stornierst, werden alle Personen auf der Warteliste mit demselben Wunschtag <strong>automatisch per E-Mail</strong> benachrichtigt.
      </p>

      <div className="mt-6 space-y-3">
        {filtered.map((w) => (
          <div key={w.id} className="bg-white border border-border rounded-md p-4 sm:p-5" data-testid={`wl-card-${w.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="font-mono text-terracotta text-lg">{w.preferred_date}</div>
                {w.preferred_time && <div className="text-xs text-ink-muted">Wunschzeit {w.preferred_time}</div>}
              </div>
              <div className="text-right">
                <span className={`text-xs px-2.5 py-1 rounded-full ${statusCls(w.status)}`}>{STATUS_LABEL[w.status]}</span>
                {w.notified_at && <div className="text-[10px] text-ink-muted mt-1">{w.notified_at.slice(0, 16).replace("T", " ")}</div>}
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-border">
              <div className="text-ink">{w.customer_name}</div>
              <div className="text-xs text-ink-muted">{w.service_name_snapshot}</div>
              <div className="mt-2 flex flex-col gap-1 text-sm">
                <a href={`tel:${w.customer_phone}`} className="text-terracotta">📞 {w.customer_phone}</a>
                <a href={`mailto:${w.customer_email}`} className="text-terracotta break-all">✉ {w.customer_email}</a>
              </div>
              {w.notes && <div className="text-xs text-ink-muted mt-2 italic">„{w.notes}"</div>}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {w.status === "waiting" && (
                <button onClick={() => notify(w.id)} className="flex-1 min-w-[140px] inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs bg-terracotta/10 text-terracotta rounded-md" data-testid={`wl-notify-${w.id}`}>
                  <Send className="w-3.5 h-3.5" strokeWidth={1.5} /> Benachrichtigen
                </button>
              )}
              {w.status === "notified" && (
                <button onClick={() => setStatus(w.id, "booked")} className="flex-1 min-w-[120px] inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs bg-sage/15 text-sage rounded-md" data-testid={`wl-booked-${w.id}`}>
                  <Check className="w-3.5 h-3.5" strokeWidth={1.5} /> Gebucht
                </button>
              )}
              {w.status !== "expired" && w.status !== "booked" && (
                <button onClick={() => setStatus(w.id, "expired")} className="inline-flex items-center justify-center gap-1 px-3 py-2 text-xs bg-muted text-ink-muted rounded-md" data-testid={`wl-expire-${w.id}`}>
                  <X className="w-3.5 h-3.5" strokeWidth={1.5} /> Ablauf
                </button>
              )}
              <button onClick={() => del(w.id)} className="inline-flex items-center justify-center px-3 py-2 text-xs bg-muted text-ink-muted rounded-md" data-testid={`wl-del-${w.id}`}><Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="text-center py-10 text-ink-muted bg-white border border-border rounded-md">—</div>}
      </div>
    </div>
  );
}
